import {ShowPageScopeCollector} from "@frontend/group/modules/scope-collectors/show-page-collector";

const getScopePage = () => ShowPageScopeCollector.get(['previews', 'updateLayoutChanges', 'canvas_data']);

// Related to Rectangle & Circle, for backwards compatability
export const parseShapeSettings = (options) => {
  const option3 = options['Option3'] || options['option3'];

  if (!option3 || typeof option3 !== 'string') {
    return {};
  }

  try {
    const parsed = JSON.parse(option3);
    return parsed && typeof parsed === 'object' && (
      Object.prototype.hasOwnProperty.call(parsed, 'stroke_color') ||
      Object.prototype.hasOwnProperty.call(parsed, 'stroke_width') ||
      Object.prototype.hasOwnProperty.call(parsed, 'fill_color') ||
      Object.prototype.hasOwnProperty.call(parsed, 'rounding')
    ) ? parsed : {};
  } catch (e) {
    return {};
  }
};

export function hexToRgb(hex) {
  const bigint = parseInt(hex.substring(1), 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}

export function rgbToHex([r, g, b]) {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export function rgbToCmyk([r, g, b]) {
  const scaledR = r / 255, scaledG = g / 255, scaledB = b / 255;
  const k = 1 - Math.max(scaledR, scaledG, scaledB);
  return [
    (1 - scaledR - k) / (1 - k) || 0,
    (1 - scaledG - k) / (1 - k) || 0,
    (1 - scaledB - k) / (1 - k) || 0,
    k
  ].map((val) => Math.round(val * 100));
}

export function cmykToRgb([c, m, y, k]) {
  c /= 100;
  m /= 100;
  y /= 100;
  k /= 100;
  return [
    255 * (1 - c) * (1 - k),
    255 * (1 - m) * (1 - k),
    255 * (1 - y) * (1 - k),
  ].map(Math.round);
}

export function updateLayoutVariantBrand(instanceId, brand, variant) {
  const { updateLayoutChanges } = getScopePage();
  
  updateLayoutChanges(instanceId, 'brand', brand);
  updateLayoutChanges(instanceId, 'variant', variant);
}

function formatRectangleName(name) {
  return name.trim().toLowerCase().split(" ").join("_");
}

export function getPrimarySecondaryColors(instanceId) {
  const {previews} = getScopePage();
  const preview = previews.find((p) => p.id === instanceId);

  if (!preview) {
    console.error(`Preview with id ${instanceId} not found.`);
    return null;
  }

  const rectangles = preview.template.fields.filter((f) => f.type === "Rectangle");
  const colors = {
    primary: {
      title: 'Primary Color',
      fill: '#000000',
      hex: '#000000',
      cmyk: '#000000',
      rgb: '#000000',
    },
    secondary: {
      title: 'Secondary Color',
      fill: '#000000',
      hex: '#000000',
      cmyk: '#000000',
      rgb: '#000000',
    }
  };

  for (const rectangle of rectangles) {
    try {
      const options = JSON.parse(rectangle.options);
      const shapeSettings = parseShapeSettings(options);
      const rectangleDataId = formatRectangleName(rectangle.name);
      const fillColor = preview.data[`rectangle_${rectangleDataId}_fill_color`];
      
      const option3 = shapeSettings.fill_color ?? options?.Option3;

      switch (option3?.toLowerCase()) {
        case 'primary':
          colors.primary.fill = fillColor || colors.primary.fill;
          break;
        case 'secondary':
          colors.secondary.fill = fillColor || colors.secondary.fill;
          break;
      }
    } catch (error) {
      console.error(`Error processing rectangle "${rectangle.name}":`, error);
    }
  }

  return colors;
}

export function getBrandVariant(instanceId) {
  const {previews} = getScopePage();
  const preview = previews.find((p) => p.id === instanceId);

  if (!preview || !preview.data) {
    console.warn(`Preview not found or data is missing for instanceId: ${instanceId}`);
    
    return {
      brand: {
        name: 'Brand',
        variant: '',
      }
    };
  }

  return {
    brand: {
      name: preview.data.brand || 'Brand',
      variant: preview.data.variant || '',
    },
  };
}

export function checkIsUseBrandColor(layoutOptions) {
  return layoutOptions?.use_color_table ?? false;
}

export function checkIsRectanglePrimaryOrSecondary(fieldOptions) {
  const shapeSettings = parseShapeSettings(fieldOptions);
  const optionValue =  shapeSettings.fill_color ?? fieldOptions?.option3;

  if (typeof optionValue === 'string') {
    return optionValue.toLowerCase() === 'primary' || optionValue.toLowerCase() === 'secondary';
  }

  return false;
}

export function isShodShowBrandColorModal(layoutOptions, fieldOptions) {
  return checkIsUseBrandColor(layoutOptions) && checkIsRectanglePrimaryOrSecondary(fieldOptions);
}
